import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useScreenData } from '../hooks/useScreenData';
import RecordActions from '../components/RecordActions';
import DetailPage, { RecordFields, orNotSet, status, when } from '../components/DetailPage';

export default function UserDetail() {
  const { id } = useParams();
  return <UserRecord key={id} id={id} />;
}

function UserRecord({ id }) {
  const screen = useScreenData(id, (options) => api.getUser(id, options));
  const { admin } = useAuth();
  const operator = admin?.displayName || 'you';

  const actionsFor = (u) => {
    const who = `${u.display_name || u.username} (${u.username}) in ${u.tenant_name}`;
    const state = {
      404: 'this member no longer exists.',
      409: `this member was already ${u.is_active ? 'locked' : 'unlocked'}. Someone may have changed it first.`,
      400: 'the server refused the reason. Write 1 to 500 characters.',
    };
    const actions = [u.is_active ? {
      key: 'lock', label: 'Lock User', className: 'btn-danger', field: 'reason', submit: 'Confirm lock',
      title: `Lock member ${u.username}`, refused: (err) => state[err.status],
      about: `${who} cannot sign in until an operator unlocks them. The audit log records this as ${operator}.`,
      send: (reason, options) => api.lockUser(id, reason.trim(), options),
      done: 'Member locked.', unknown: 'the member was locked',
    } : {
      key: 'unlock', label: 'Unlock User', className: 'btn-success', submit: 'Confirm unlock',
      title: `Unlock member ${u.username}`, refused: (err) => state[err.status],
      // Sign-in needs the tenant unlocked too (auth.service.js login query); getUser returns tenant_active.
      about: `${who} will have their member lock removed. ${u.tenant_active ? '' : `The tenant ${u.tenant_name} remains locked. `}Other sign-in requirements still apply. The audit log records this as ${operator}.`,
      send: (_none, options) => api.unlockUser(id, options),
      done: 'Member unlocked.', unknown: 'the member was unlocked',
    }];
    if (u.auth_provider === 'local') {
      actions.push({
        key: 'password', label: 'Reset Password', className: 'btn-outline', field: 'password', submit: 'Set new password',
        title: `Set a new password for ${u.username}`,
        about: `This sets the password of ${who} now, and their old one stops working. It sends no email: give them the new password yourself. The audit log records that it changed, never the password, as ${operator}.`,
        // Fixed sentences only: a server message could repeat what was sent.
        refused: (err) => ({
          404: 'this member no longer exists.',
          400: err.data?.type === 'PLATFORM_SSO_ACCOUNT'
            ? 'this member signs in with an outside provider, so no password can be set here.'
            : 'the server refused this password. Use 8 to 72 characters, at most 72 bytes.',
        })[err.status],
        send: (password, options) => api.resetUserPassword(id, password, options),
        done: 'Password set.', unknown: 'the password was set',
      });
    }
    return actions;
  };

  return (
    <DetailPage screen={screen} noun="Member" back={{ to: '/users', label: 'Back to members' }}
      title={({ user }) => user.display_name || user.username}
      actions={(record, hold) => <RecordActions actions={record ? actionsFor(record.user) : []} disabled={hold}
        refreshing={screen.loading} readSucceeded={Boolean(screen.data) && !screen.stale && !screen.error}
        isFresh={screen.isFresh} onDone={screen.reload} />}>
      {({ user: u }) => (
        <>
          {u.auth_provider !== 'local' && (
            <p className="field-hint">No password can be set here: this member signs in with {u.auth_provider}.</p>
          )}
          <RecordFields label="Member record" rows={[
            ['Username', u.username],
            ['Member ID', <span className="record-id">{u.id}</span>],
            ['Name', orNotSet([u.first_name, u.last_name].filter(Boolean).join(' '))],
            ['Display name', orNotSet(u.display_name)],
            ['Email', orNotSet(u.email)],
            ['Role', u.role],
            ['Status', status(u.is_active)],
            ['Sign-in', u.auth_provider],
            ['Tenant', <><Link to={`/tenants/${u.tenant_id}`}>{u.tenant_name}</Link> ({u.tenant_slug})</>],
            ['Tenant plan', <span className="badge badge-plan">{u.tenant_plan}</span>],
            ['Tenant status', status(u.tenant_active)],
            ['Avatar', orNotSet(u.avatar_url)],
            ['Last sign-in', when(u.last_login, 'Never')],
            ['Created', when(u.created_at)],
          ]} />
        </>
      )}
    </DetailPage>
  );
}
